import React, { useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../common/Button'
import { Icon, IconChoices } from '../../common/Icon'
import { Modal } from '../../common/Modal'
import { ModalBody } from '../../common/Modal/ModalBody'
import { ModalHeader } from '../../common/Modal/ModalHeader'
import { Spinner } from '../../common/Spinner'
import { isDeniedRequest } from '../../../api/helpers'
import { PeopleSource, peopleQueries } from '../../../api/people'
import { PersonListItem } from './PersonListItem'

interface PeopleModalProps {
  icon: IconChoices
  iconHighlighted?: boolean
  title: string
  source: PeopleSource
  close: () => void
}

export const PeopleModal = ({
  icon,
  iconHighlighted,
  title,
  source,
  close,
}: PeopleModalProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const ref = useRef(null)
  const {
    data,
    isPending,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(peopleQueries.page(source))

  const withdrawn = isError && isDeniedRequest(error)
  const societyId = withdrawn ? undefined : data?.pages[0]?.societyId
  const people = withdrawn
    ? []
    : data?.pages.flatMap((page) => page.items) ?? []
  const showEmpty =
    !isPending && !isError && !hasNextPage && people.length === 0

  return (
    <Modal ref={ref} show size="sm">
      {{
        header: (
          <ModalHeader onClose={close}>
            <div className="flex gap-3">
              <Icon icon={icon} highlighted={iconHighlighted} />
              <p style={theme.textVariants.lead}>{title}</p>
            </div>
          </ModalHeader>
        ),
        body: (
          <ModalBody className="max-h-96 overflow-y-auto">
            {isPending && (
              <div className="flex justify-center p-4">
                <Spinner />
              </div>
            )}
            {isError && (
              <div className="flex flex-col items-center gap-3 p-4">
                <p style={theme.textVariants.base}>
                  {translate('flashMessage.somethingWentWrongError')}
                </p>
                {!withdrawn && (
                  <Button
                    label={translate('common.actions.tryAgain')}
                    size="sm"
                    onClick={() => refetch()}
                  />
                )}
              </div>
            )}
            {showEmpty && (
              <p className="p-4" style={theme.textVariants.base}>
                {translate('post.peopleEmpty')}
              </p>
            )}
            <div className="flex flex-col">
              {societyId &&
                people.map((person, index) => (
                  <PersonListItem
                    key={person.id}
                    person={person}
                    societyId={societyId}
                    index={index}
                  />
                ))}
            </div>
            {!withdrawn && hasNextPage && (
              <Button
                className="flex w-fit"
                variant={ButtonVariant.TEXT}
                label={translate('common.showMore')}
                loading={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
